import { AnyAction, createAction } from '@reduxjs/toolkit';
import { StateObservable, combineEpics } from 'redux-observable';
import { Observable, concatMap, filter, forkJoin, map, mergeMap } from 'rxjs';
import { SmoLanguage } from '../../../../../Models/Smo/SmoLanguage';
import { SmoMoonType } from '../../../../../Models/Smo/SmoMoonType';
import { rangeArray } from '../../../../../Utils/Array';
import { IS_MAC_OS } from '../../../../../Utils/Constants';
import { mapPayload } from '../../../../../Utils/RxjsOperators/MapPayload';
import { ImageComparator } from '../../../../ImageComparator/ImageComparator';
import { ImageCropper } from '../../../../ImageCropper/ImageCropper';
import { ImageFilterer } from '../../../../ImageFilterer/ImageFilterer';
import { TextProcessingManager } from '../../../../TextProcessing/TextProcessingManager';
import { StoreDependencies } from '../../StoreDependencies';
import { StoreState } from '../../StoreState';
import VideoAnalysisActions from '../VideoAnalysisActions';
import { getFilterBrightness, getUserSmoLanguage, processImage } from './VideoAnalysisEpics';

const compareMoonTitleAction = createAction<{
  image: HTMLImageElement;
  moonType: SmoMoonType;
  filterBrightness: number;
}>('COMPARE_MOON_TITLE');
const calibrateAndCompareMoonTitleAction = createAction<{ image: HTMLImageElement; moonType: SmoMoonType }>(
  'CALIBRATE_AND_COMPARE_MOON_TITLE'
);
const maximumMisMatchPercent: number = 10;

function readMoonTitle(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  _dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(VideoAnalysisActions.readMoonTitle.match),
    mapPayload(),
    mergeMap(({ image, moonType }) =>
      getFilterBrightness(moonType, $state).pipe(map((filterBrightness) => ({ image, moonType, filterBrightness })))
    ),
    map(({ image, moonType, filterBrightness }) =>
      filterBrightness
        ? compareMoonTitleAction({ image, moonType, filterBrightness })
        : calibrateAndCompareMoonTitleAction({ image, moonType })
    )
  );
}

function compareMoonTitle(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(compareMoonTitleAction.match),
    mapPayload(),
    mergeMap(({ image, moonType, filterBrightness }) =>
      processAndCompareImageWithBase(
        image,
        moonType,
        filterBrightness,
        getUserSmoLanguage($state),
        dependencies.textProcessingManager!,
        dependencies.imageCropper!,
        dependencies.imageFilterer!,
        dependencies.imageComparator!
      ).pipe(
        filter((result) => result.comparisonMisMatchPercent < maximumMisMatchPercent),
        map(() => VideoAnalysisActions.readMoonName({ image, moonType, filterBrightness }))
      )
    )
  );
}

function calibrateAndCompareMoonTitle(
  $action: Observable<AnyAction>,
  $state: StateObservable<StoreState>,
  dependencies: Partial<StoreDependencies>
): Observable<AnyAction> {
  return $action.pipe(
    filter(calibrateAndCompareMoonTitleAction.match),
    mapPayload(),
    mergeMap(({ image, moonType }) =>
      forkJoin(
        getBrightnessRange().map((brightness) =>
          processAndCompareImageWithBase(
            image,
            moonType,
            brightness,
            getUserSmoLanguage($state),
            dependencies.textProcessingManager!,
            dependencies.imageCropper!,
            dependencies.imageFilterer!,
            dependencies.imageComparator!
          )
        )
      ).pipe(
        map((results) => results.sort((a, b) => a.comparisonMisMatchPercent - b.comparisonMisMatchPercent)[0]),
        filter((bestResult) => bestResult.comparisonMisMatchPercent < maximumMisMatchPercent),
        concatMap((bestResult) => [
          VideoAnalysisActions.setFilterBrightness({
            brightness: bestResult.filterBrightness,
            comparisonMisMatchPercent: bestResult.comparisonMisMatchPercent,
            moonType: moonType
          }),
          VideoAnalysisActions.readMoonName({ image: image, moonType, filterBrightness: bestResult.filterBrightness })
        ])
      )
    )
  );
}

function processAndCompareImageWithBase(
  image: HTMLImageElement,
  moonType: SmoMoonType,
  filterBrightness: number,
  language: SmoLanguage,
  textProcessingManager: TextProcessingManager<SmoMoonType>,
  imageCropper: ImageCropper,
  imageFilterer: ImageFilterer,
  imageComparator: ImageComparator<SmoLanguage>
): Observable<{ filterBrightness: number; comparisonMisMatchPercent: number }> {
  return processImage(
    image,
    textProcessingManager.processingData[moonType].titleFrame(filterBrightness),
    imageCropper,
    imageFilterer
  ).pipe(
    mergeMap((processedImage) =>
      imageComparator.getBaseComparisonImage(moonType, language).pipe(
        mergeMap((baseImage) => imageComparator.compareImages(processedImage, baseImage)),
        map((comparisonMisMatchPercent) => ({ filterBrightness, comparisonMisMatchPercent }))
      )
    )
  );
}

function getBrightnessRange(): number[] {
  return IS_MAC_OS ? rangeArray(-0.46, -0.44, 0.01) : rangeArray(-0.5, -0.47, 0.01);
}

export default combineEpics(readMoonTitle, compareMoonTitle, calibrateAndCompareMoonTitle);
